import { CommonModule } from '@angular/common';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { DestroyableComponent } from '../../core/utils/destroyable.component';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CreateWalletRequest } from '../interfaces/createWalletRequest';
import { CompanyService } from '../../providers/company/company.service';
import { NotificationService } from '../../providers/notification/notification.service';

import { CompanyDTO } from '../../interfaces/companyDTO';
import { STATES } from '../../constants';
import { CustomControlsModule } from '../../core/modules/custom-controls/custom-controls.module';

interface DialogData {
  company: CompanyDTO;
  routingNumber: string;
  accountNumber: string;
}

type BraintreeModule = typeof import('braintree-web');

@Component({
  selector: 'app-create-wallet',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    CustomControlsModule,
  ],
  templateUrl: './create-wallet.component.html',
  styleUrls: ['./create-wallet.component.scss'],
})
export class CreateWalletComponent extends DestroyableComponent implements OnInit {
  @Input() clientData = null;
  public createWalletForm = this.buildCreateWalletForm();

  constructor(
    private readonly fb: FormBuilder,
    private companyService: CompanyService,
    private notificationService: NotificationService,
    public dialogRef: MatDialogRef<CreateWalletComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) {
    super();
  }

  usBankAccountFun: {
    tokenize: (tokenizeParams: unknown) => Promise<{ nonce: string }>;
  } | null = null;

  ngOnInit() {
    this.companyService.getToken().subscribe(
      (res) => {
        this.clientData = res;
        void this.initializeUsBankAccount(this.clientData.clientToken);
      },
      () => { },
    );
  }

  private async initializeUsBankAccount(clientToken: string) {
    let braintree: BraintreeModule;

    try {
      braintree = await import('braintree-web');
    } catch {
      return;
    }

    braintree.client.create({
      authorization: clientToken,
    }, (err, clientInstance) => {
      if (err || !clientInstance) {
        return;
      }

      braintree.usBankAccount.create({
        client: clientInstance,
      }).then((usBankAccountInstance) => {
        this.usBankAccountFun = usBankAccountInstance;
      }).catch(() => {
        this.usBankAccountFun = null;
      });
    });
  }

  public onSubmit() {
    this.addACH();
  }

  addACH() {
    if (!!this.usBankAccountFun) {
      const { accountNumber,
        routingNumber,
        ownershipType,
        firstName,
        lastName,
        streetAddress,
        locality,
        region,
        postalCode,
        businessName,
      } = this.createWalletForm.value;

      const bankDetails = {
        routingNumber,
        ownershipType,
        firstName,
        lastName,
        accountNumber,
        businessName,
        accountType: 'checking',
        billingAddress: {
          streetAddress,
          locality,
          region,
          postalCode,
        },
      };

      const tokenizeParams = {
        bankDetails,
        mandateText: 'I authorize Braintree to debit my bank account on behalf of My Online Store.'
      };

      this.usBankAccountFun.tokenize(tokenizeParams).then((tokenizeResponse) => {
        this.dialogRef.close({ token: tokenizeResponse.nonce, type: 'ACH' } as CreateWalletRequest);
      }).catch((err) => {
        const errorMessage = err.details.originalError.details.originalError[0].message;
        this.notificationService.error(errorMessage);
      });
    }

    return false;
  }

  private getAbbreviation(state: string) {
    const stateModel = STATES.find(x => x.name === state);

    if (!stateModel) {
      return state;
    }

    return stateModel.abbreviation;
  }

  private buildCreateWalletForm() {
    return this.fb.group({
      firstName: new FormControl(this.data.company.contactPersonFirstName, [Validators.minLength(0)]),
      lastName: new FormControl(this.data.company.contactPersonLastName, [Validators.minLength(0)]),
      ownershipType: new FormControl('business', [Validators.required]),
      businessName: new FormControl(this.data.company.name, [Validators.required]),
      accountNumber: new FormControl(this.data.accountNumber, [Validators.required, Validators.minLength(1),
      Validators.maxLength(17)]),
      routingNumber: new FormControl(this.data.routingNumber, [Validators.required, Validators.minLength(8),
      Validators.maxLength(9)]),
      streetAddress: new FormControl(this.data.company.address, [Validators.minLength(0)]),
      locality: new FormControl(this.data.company.city, [Validators.minLength(0)]),
      region: new FormControl(this.getAbbreviation(this.data.company.state), [Validators.required, Validators.minLength(0)]),
      postalCode: new FormControl(this.data.company.zip, [Validators.required, Validators.minLength(0)]),
    });
  }
}
